/**
 * Le regole sui contenuti, accettate prima del primo contenuto che parte (#65).
 *
 * Apple (1.2) e Google Play chiedono che chi carica contenuti accetti le regole
 * **prima** di creare o caricare. Il controllo sta qui ed è chiamato dai due
 * punti da cui passa per forza ogni contenuto — `createPuzzle` e
 * `uploadPuzzleMedia` — così vale per ogni ingresso dell'app, compresi quelli
 * che verranno.
 *
 * Il servizio non sa disegnare: la richiesta all'utente la registra l'app
 * (`registraRichiestaConsenso`). Se nessuno l'ha registrata, si ferma — meglio
 * un errore che un contenuto pubblicato senza consenso.
 */
import logger from "./logger";

const CHIAVE = "puzzly.contentRulesAccepted";

let accettate = false;
let chiedi = null;

export class ContentRulesError extends Error {
  constructor(code) {
    super(
      code === "declined"
        ? "You need to accept the content rules to publish."
        : "The content rules couldn't be shown — please try again."
    );
    this.code = code === "declined" ? "content-rules-declined" : "content-rules-unavailable";
  }
}

function giaAccettateQui() {
  try {
    return window.localStorage.getItem(CHIAVE) === "1";
  } catch (_) {
    return false;
  }
}

function segna() {
  accettate = true;
  try {
    window.localStorage.setItem(CHIAVE, "1");
  } catch (_) {
    /* senza storage resta valido per la sessione */
  }
}

/** «Non ora» sulle regole: per chi chiama è un annullamento, non un guasto. */
export function isRulesDeclined(err) {
  return !!err && err.code === "content-rules-declined";
}

/** L'app registra qui la richiesta all'utente: `() => Promise<boolean>`. */
export function registraRichiestaConsenso(fn) {
  chiedi = fn;
}

/**
 * Risolve se le regole sono accettate; altrimenti le chiede. Lancia
 * `ContentRulesError` se l'utente rifiuta o se nessuno può chiederle.
 *
 * @param {{profile?: object}} [opts] — se il profilo dice già che le ha
 *        accettate (su un altro dispositivo), non si richiede.
 */
export async function requireContentRulesAccepted({ profile } = {}) {
  if (accettate || giaAccettateQui()) return;
  if (profile && profile.contentRulesAcceptedAt) {
    segna();
    return;
  }
  if (!chiedi) throw new ContentRulesError("unavailable");

  const ok = await chiedi();
  if (!ok) throw new ContentRulesError("declined");
  segna();

  // Traccia lato server, con l'ora del server (le regole non accettano altro).
  // Se fallisce l'accettazione resta valida qui: il gesto dell'utente c'è stato.
  try {
    const [{ auth, db }, { doc, updateDoc, serverTimestamp }] = await Promise.all([
      import("./firebase"),
      import("firebase/firestore"),
    ]);
    const uid = auth.currentUser && auth.currentUser.uid;
    if (uid) await updateDoc(doc(db, "users", uid), { contentRulesAcceptedAt: serverTimestamp() });
  } catch (err) {
    logger.warn("contentRules: accettazione non registrata sul profilo", { error: err.message });
  }
}

/** Solo per i test. */
export function __impostaPerITest(stato) {
  accettate = !!stato;
  chiedi = null;
  try {
    window.localStorage.removeItem(CHIAVE);
  } catch (_) {
    /* ambiente senza window */
  }
}
